// Stories 027 + 065 — header buttons for state transitions.
//
// Composes three distinct affordances onto the right end of the
// AnalysisHeader strip:
//
//   * MarkCompleteButton (Story 065) — phase-aware label, gated on
//     100%-viewed progress.
//   * ApproveButton (Story 027) — PM/admin only, visible at
//     EVALUATION_DONE.
//
// SB6-ANA-HEADER-02 — the Reject action (Story 027) moved OUT of this
// header strip into the Asset Info burger menu (AssetInfoBurgerMenu.tsx);
// its confirm dialog + reason/target-phase flow + PM/admin-and-phase
// gating are unchanged, only the placement. The Mark-Complete CTA and
// Approve stay here as the primary workflow buttons.
//
// Each button opens its own confirm dialog. Snackbars on
// success/failure live here too (single Snackbar slot — show one at a
// time; later actions replace earlier ones).
import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useApproveDataAcquisitionMutation,
  useMarkDataAcquisitionCompleteMutation,
} from "../workflowMutations.js";
// ANA-R1-11 — the visible header CTAs (Mark … Complete / Sign Off & Lock and
// Approve) conform to the agreed analysis-surface action-button style. The
// in-dialog confirm/cancel buttons are modal controls, not surface actions,
// and keep the default MUI size.
// UIOPT-01 (2026-06-09) — Christian: the 24px `compactButtonXsSx` read like a
// tag next to the pickers. Size these filled CTAs to `compactFilledCtaSx`
// (the shared dense control-row height) so they match the catalogue /
// class-model dropdowns instead.
import { compactFilledCtaSx } from "@aeriosiq/ui";
// R58-L-03 — the Mark Prescreening Complete gate consumes the same
// L-02-keyed progress query as the standalone ReviewProgressBar, so the
// button enables in the same render frame the bar fills to 100 %. The
// legacy `useReviewProgressQuery` is left in place for any non-gate
// consumers (none today) so its analysisKeys.detail invalidation chain
// isn't broken.
import { useSelfReviewProgress } from "./ReviewProgressBar.js";

// SB3-CC-G (lyse/v1@6.0.0): unified single-axis 8-value vocabulary
// per I5 supersedure. The analyst sub-workflow values are inlined.
type DataAcquisitionState =
  | "CREATED"
  | "PROCESSING"
  | "PRE_SCREENING"
  | "EXPERT_ANALYSIS"
  | "EVALUATION_DONE"
  | "LOCKED"
  | "ISSUE"
  | "REJECTED";

export interface WorkflowHeaderActionsProps {
  dataAcquisitionId: string;
  evaluationState: DataAcquisitionState | null;
  prescreenerUserId: string | null;
  expertUserId: string | null;
  meId: string | null | undefined;
  meRoles: readonly string[] | undefined;
}

interface MarkCompletePlan {
  label: string;
  fromPhase: "PRE_SCREENING" | "EXPERT_ANALYSIS" | "EVALUATION_DONE";
  dialogTitle: string;
  dialogBody: string;
  dialogConfirmLabel: string;
}

function planMarkComplete(args: {
  evaluationState: DataAcquisitionState | null;
  prescreenerUserId: string | null;
  expertUserId: string | null;
  meId: string | null | undefined;
  meRoles: readonly string[] | undefined;
}): MarkCompletePlan | null {
  const { evaluationState, prescreenerUserId, expertUserId, meId, meRoles } =
    args;
  if (!evaluationState || !meId) return null;
  const isAdmin =
    (meRoles?.includes("tenant_admin") ?? false) ||
    (meRoles?.includes("project_manager") ?? false);
  if (evaluationState === "PRE_SCREENING") {
    if (prescreenerUserId !== meId) return null;
    return {
      label: "Mark Pre-Screening Complete",
      fromPhase: "PRE_SCREENING",
      dialogTitle: "Mark Pre-Screening complete",
      dialogBody:
        "This transitions the data acquisition to Expert Analysis and releases your edit lock.",
      dialogConfirmLabel: "Mark Pre-Screening Complete",
    };
  }
  if (evaluationState === "EXPERT_ANALYSIS") {
    if (expertUserId !== meId) return null;
    return {
      label: "Mark Expert Analysis Complete",
      fromPhase: "EXPERT_ANALYSIS",
      dialogTitle: "Mark Expert Analysis complete",
      dialogBody:
        "This freezes the defect set and transitions the data acquisition to Evaluation Done for sign-off.",
      dialogConfirmLabel: "Mark Expert Analysis Complete",
    };
  }
  if (evaluationState === "EVALUATION_DONE") {
    if (!isAdmin) return null;
    return {
      label: "Sign Off & Lock",
      fromPhase: "EVALUATION_DONE",
      dialogTitle: "Sign off and lock",
      dialogBody:
        "After sign-off the defect set is immutable and the data acquisition is read-only.",
      dialogConfirmLabel: "Sign Off & Lock",
    };
  }
  return null;
}

export function WorkflowHeaderActions({
  dataAcquisitionId,
  evaluationState,
  prescreenerUserId,
  expertUserId,
  meId,
  meRoles,
}: WorkflowHeaderActionsProps): React.ReactElement {
  const isPm =
    (meRoles?.includes("project_manager") ?? false) ||
    (meRoles?.includes("tenant_admin") ?? false);
  const markCompletePlan = planMarkComplete({
    evaluationState,
    prescreenerUserId,
    expertUserId,
    meId,
    meRoles,
  });
  // Only poll review-progress when the page might surface it — Story
  // 010 AC3 drag-to-rearrange depends on a stable render tree on the
  // LOCKED detail stub, so a 404'ing useQuery during drag stalls RGL.
  // R58-L-03 — switched to useSelfReviewProgress so the L-02 upsert
  // invalidation (`["review-progress", daId, "self"]`) drives the gate
  // in lock-step with the standalone progress bar.
  const progressEnabled = markCompletePlan != null;
  const progress = useSelfReviewProgress(
    progressEnabled ? dataAcquisitionId : "",
  );

  const total = progress.data?.total ?? 0;
  const viewed = progress.data?.viewedCount ?? 0;
  // Sign-Off & Lock at EVALUATION_DONE: progress denominator is 0
  // (frozen set). Treat as "complete" so the button enables.
  const percent =
    evaluationState === "EVALUATION_DONE"
      ? 100
      : total > 0
        ? Math.round((viewed / total) * 100)
        : 0;
  const isComplete = percent >= 100;

  // Approve (Story 027) — PM/admin only, EVALUATION_DONE only.
  const showApprove = isPm && evaluationState === "EVALUATION_DONE";
  // SB6-ANA-HEADER-02 — Reject (Story 027) relocated to the Asset Info
  // burger menu; the PM/admin + phase gate now lives there unchanged.

  const [completeOpen, setCompleteOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [acknowledgeUnseen, setAcknowledgeUnseen] = useState(false);

  const [snack, setSnack] = useState<
    { kind: "success"; text: string } | { kind: "error"; text: string } | null
  >(null);

  const approveMutation = useApproveDataAcquisitionMutation();
  const markCompleteMutation = useMarkDataAcquisitionCompleteMutation();

  // Story 010 AC3 — keep the DOM lean when no buttons would render so
  // the RGL drag-handle gesture isn't competing with offscreen portal
  // dialogs / hidden snackbars for keyboard / pointer events.
  if (!markCompletePlan && !showApprove) {
    return <Stack direction="row" sx={{ flexShrink: 0 }} />;
  }

  const onMarkCompleteConfirm = (): void => {
    if (!markCompletePlan) return;
    setCompleteOpen(false);
    markCompleteMutation.mutate(
      { dataAcquisitionId, fromPhase: markCompletePlan.fromPhase },
      {
        onSuccess: (data) => {
          setSnack({
            kind: "success",
            text: `Marked ${data.fromPhase.replace("_", " ").toLowerCase()} complete`,
          });
        },
        onError: () => {
          setSnack({
            kind: "error",
            text: "Could not mark complete — please retry",
          });
        },
      },
    );
  };

  const onApproveConfirm = (): void => {
    setApproveOpen(false);
    approveMutation.mutate(
      { dataAcquisitionId, acknowledgeUnseen },
      {
        onSuccess: () => {
          setSnack({ kind: "success", text: "Data acquisition approved" });
        },
        onError: () => {
          setSnack({ kind: "error", text: "Could not approve — please retry" });
        },
      },
    );
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ flexShrink: 0 }}
    >
      {markCompletePlan ? (
        <Tooltip
          // R58-L-03 AC3 — when disabled, the tooltip names the count of
          // unseen images. Wording changed from "View all N findings/
          // defects to enable completion." to match AC3 verbatim
          // ("{unseenCount} image{s} still unseen").
          title={
            isComplete
              ? ""
              : `${String(Math.max(total - viewed, 0))} image${
                  total - viewed === 1 ? "" : "s"
                } still unseen`
          }
        >
          <span>
            <Button
              variant="contained"
              size="small"
              sx={compactFilledCtaSx}
              onClick={() => {
                setCompleteOpen(true);
              }}
              disabled={!isComplete}
              aria-disabled={!isComplete}
              data-testid="mark-prescreening-complete-button"
            >
              {markCompletePlan.label}
            </Button>
          </span>
        </Tooltip>
      ) : null}
      {showApprove ? (
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={compactFilledCtaSx}
          onClick={() => {
            setApproveOpen(true);
          }}
        >
          Approve
        </Button>
      ) : null}
      {/* Story 065 — confirm dialog. */}
      {markCompletePlan ? (
        <Dialog
          open={completeOpen}
          onClose={() => {
            setCompleteOpen(false);
          }}
          aria-labelledby="mark-complete-title"
        >
          <DialogTitle id="mark-complete-title">
            {markCompletePlan.dialogTitle}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ minWidth: 380 }}>
              <Typography>{markCompletePlan.dialogBody}</Typography>
              {evaluationState !== "EVALUATION_DONE" ? (
                <Typography variant="body2" color="text.secondary">
                  {viewed} of {total} viewed ({percent}%)
                </Typography>
              ) : null}
              <LinearProgress
                variant="determinate"
                value={percent}
                aria-hidden="true"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setCompleteOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button variant="contained" onClick={onMarkCompleteConfirm}>
              {markCompletePlan.dialogConfirmLabel}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}

      {/* Story 027 — Approve dialog. */}
      <Dialog
        open={approveOpen}
        onClose={() => {
          setApproveOpen(false);
        }}
        aria-labelledby="approve-title"
      >
        <DialogTitle id="approve-title">Approve data acquisition?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ minWidth: 380 }}>
            <Typography>
              This sets the data acquisition to Locked and stops further
              editing.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledgeUnseen}
                  onChange={(e) => {
                    setAcknowledgeUnseen(e.target.checked);
                  }}
                />
              }
              label="I acknowledge there may be unreviewed items"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApproveOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={onApproveConfirm}>
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {snack ? (
        <Snackbar
          open
          autoHideDuration={snack.kind === "error" ? 6000 : 3000}
          onClose={() => {
            setSnack(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={snack.kind}
            variant="filled"
            onClose={() => {
              setSnack(null);
            }}
          >
            {snack.text}
          </Alert>
        </Snackbar>
      ) : null}
    </Stack>
  );
}
